/**
 * Pilot V2 HTTP client.
 * This is the ONLY place in V2 that calls fetch.
 * All service files (user, store, commerce) call v2Request — never fetch directly.
 */

const BASE_URL = (process.env.REACT_APP_PILOT_URL || '').replace(/\/$/, '');

/** In-memory JWT for Pilot requests; kept in sync with V2SessionProvider */
let v2BearerToken = null;

export function setV2BearerToken(token) {
  v2BearerToken = token ? String(token) : null;
}

/** Normalised error shape returned to every caller */
export class V2ApiError extends Error {
  constructor({ status, code, message, recoverable = true }) {
    super(message);
    this.name = 'V2ApiError';
    this.status = status;
    this.code = code;
    this.recoverable = recoverable;
  }
}

function buildHeaders(extra = {}) {
  const headers = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    ...extra,
  };
  if (v2BearerToken && !extra.Authorization) {
    headers.Authorization = `Bearer ${v2BearerToken}`;
  }
  return headers;
}

/**
 * Core request function.
 *
 * @param {string} path        - e.g. '/api/v2/landing'
 * @param {{ method?, body?, headers?, timeoutMs? }} options
 * @returns {Promise<any>}     - Parsed JSON response body
 * @throws {V2ApiError}        - Always throws a V2ApiError on failure
 */
export async function v2Request(path, options = {}) {
  const { method = 'GET', body, headers: extraHeaders = {}, timeoutMs = 15000 } = options;

  const controller = new AbortController();
  const timerId = setTimeout(() => controller.abort(), timeoutMs);

  let response;
  try {
    response = await fetch(`${BASE_URL}${path}`, {
      method,
      headers: buildHeaders(extraHeaders),
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal: controller.signal,
    });
  } catch (err) {
    clearTimeout(timerId);
    if (err.name === 'AbortError') {
      throw new V2ApiError({ status: 0, code: 'TIMEOUT', message: 'Request timed out. Please try again.', recoverable: true });
    }
    throw new V2ApiError({ status: 0, code: 'NETWORK_ERROR', message: 'No internet connection or server unreachable.', recoverable: true });
  }

  clearTimeout(timerId);

  let data;
  const contentType = response.headers.get('content-type') || '';
  try {
    data = contentType.includes('application/json') ? await response.json() : await response.text();
  } catch {
    throw new V2ApiError({ status: response.status, code: 'PARSE_ERROR', message: 'Unexpected server response.', recoverable: false });
  }

  if (!response.ok) {
    const message = data?.message || data?.error || `Server error (${response.status})`;
    const code = data?.code || `HTTP_${response.status}`;
    const recoverable = response.status >= 500 || response.status === 429;
    throw new V2ApiError({ status: response.status, code, message, recoverable });
  }

  return data;
}
